import { useEffect, useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, DChip, Icon, Modal } from '@/components/kit';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { useMembers } from '../lib/useManagerData';
import { SettingsRail } from './SettingsRail';
import { TextEditor } from './editors';
import { SettingsUnreadable } from './Unreadable';
import { useFacility, useMoneyPolicy, useSaveMoneyPolicy, type MoneyPolicy } from './useSettings';

/**
 * Clinic manager · Settings ▸ Money — `g_set_money.dc.html`.
 *
 * FOUR POLICIES, ONE ROW. Tax, currency format, doctor shares and desk policy
 * are one `system_settings['money.policy']` document, written whole, because
 * the board's single Save writes the screen and a per-field PUT would make
 * "turned this off" and "did not mention it" the same message.
 *
 * WHAT EACH POLICY REACHES TODAY, stated because §4.8 does not let a switch
 * imply more than it does:
 *   • the CURRENCY FORMAT is live on this surface — every figure the manager's
 *     own screens print is formatted through it;
 *   • the DISCOUNT POLICY is what Reports ▸ Discounts, refunds & edits raises;
 *   • VAT, the tax ID and the day-close pair are stored and nothing reads them
 *     yet: the invoice form carries its own hard-coded 14% and there is no
 *     day-close model at all. Both are marked on the screen and written up in
 *     reports/clinic-manager.md rather than presented as live policy.
 *   • the SHARES are real and stored per doctor; there is still no payout run
 *     to apply them to, which the Revenue report already says.
 */

type Edit = 'vat' | 'taxId' | 'dayClose' | null;

/** "EGP 450" / "450 EGP" / "EGP 450.00" — the format, shown as its own example. */
function sample(currency: string, position: 'before' | 'after', decimals: 0 | 2): string {
  const n = decimals === 2 ? '450.00' : '450';
  return position === 'before' ? `${currency} ${n}` : `${n} ${currency}`;
}

/** "11:00 PM" from "23:00". */
function clock12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function ManagerMoneyPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const { value, isLoading, error, refetch } = useMoneyPolicy();
  const { facility } = useFacility();
  const { members } = useMembers();
  const save = useSaveMoneyPolicy();
  const [draft, setDraft] = useState<MoneyPolicy | null>(null);
  const [editing, setEditing] = useState<Edit>(null);
  const [shareFor, setShareFor] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [bad, setBad] = useState<string | null>(null);
  const [explaining, setExplaining] = useState(false);

  usePageMeta({ title: 'Money · System Settings' });

  useEffect(() => { if (value && !draft) setDraft(value); }, [value, draft]);

  const shown = draft ?? value;
  const dirty = !!draft && !!value && JSON.stringify(draft) !== JSON.stringify(value);
  const reading = isLoading && !shown;
  const unreadable = !reading && !shown;
  const patch = (next: Partial<MoneyPolicy>) => setDraft((d) => (d ? { ...d, ...next } : d));

  const currency = (facility?.billingCurrency || 'EGP').toUpperCase();

  const doctors = useMemo(
    () => members
      .filter((m) => m.roleName === 'Doctor' && m.status === 'active')
      .map((m) => ({
        id: m.userId,
        name: [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || m.email || 'This doctor',
      })),
    [members],
  );

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const showTax = matches('tax e-invoice vat tax id');
  const showFormat = matches('currency format symbol decimals');
  const showShares = matches('doctor payout shares');
  const showPolicy = matches('policy at the desk discount day close');
  const anyShown = showTax || showFormat || showShares || showPolicy;

  const crumb = usePhoneCrumb('Settings', 'Money');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Money',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Money
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="money" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft ? {
        label: save.isPending ? 'Saving…' : 'Save',
        onClick: () => save.mutate(draft, { onSuccess: (next) => setDraft(next) }),
      } : undefined,
    },
    [query, dirty, save.isPending, !!draft, unreadable],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s money policy…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="money policy" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {shown ? (
          <>
            {showTax ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Tax &amp; e-invoice</p>
                <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('vat')}>
                    <p className="t-ovl mut">VAT</p>
                    <p className="t-strong mt4">
                      {shown.vatPercent === null
                        ? <span className="mut">Not set</span>
                        : `${shown.vatPercent}%`} <Icon name="pencil" size="sm" /> · per tax class
                    </p>
                  </Card>
                  {/* The board draws Tax ID as a plain card. It is the only
                      value on this screen with nowhere else to be set, so it
                      carries the same pencil every other settable card does —
                      the one deviation from the artboard here, recorded in the
                      report rather than left as a value nobody can enter. */}
                  <Card style={{ flex: 1.2, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('taxId')}>
                    <p className="t-ovl mut">Tax ID</p>
                    <p className="t-strong mt4">
                      {shown.taxId ?? <span className="mut">Not set</span>} <Icon name="pencil" size="sm" />
                    </p>
                  </Card>
                  <Card style={{ flex: 1.2, margin: 0 }}>
                    <p className="t-ovl mut">ETA e-invoice</p>
                    <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {([['not_started', 'Not started'], ['configured', 'Configured'], ['live', 'Live']] as const).map(([k, label]) => (
                        <Chip key={k}
                          kind={(shown.etaStatus ?? 'not_started') === k ? 'brand' : 'outline'} small
                          className={(shown.etaStatus ?? 'not_started') === k ? 'w6' : undefined}
                          onClick={() => patch({ etaStatus: k })}>
                          {label}
                        </Chip>
                      ))}
                    </div>
                  </Card>
                </div>
              </>
            ) : null}

            {showFormat ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
                  Currency format — what every bill, statement and payslip prints
                </p>
                <Card>
                  <p className="t-ovl mut mb8">Symbol</p>
                  <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(['before', 'after'] as const).map((pos) => (
                      <Chip key={pos}
                        kind={shown.symbolPosition === pos ? 'brand' : 'outline'} small
                        className={shown.symbolPosition === pos ? 'w6' : undefined}
                        onClick={() => patch({ symbolPosition: pos })}>
                        {pos === 'before' ? 'Before' : 'After'} — {sample(currency, pos, shown.decimals)}
                      </Chip>
                    ))}
                  </div>
                  <p className="t-ovl mut" style={{ margin: '14px 0 8px' }}>Decimals</p>
                  <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {([0, 2] as const).map((d) => (
                      <Chip key={d}
                        kind={shown.decimals === d ? 'brand' : 'outline'} small
                        className={shown.decimals === d ? 'w6' : undefined}
                        onClick={() => patch({ decimals: d })}>
                        {d} — {sample(currency, shown.symbolPosition, d)}
                      </Chip>
                    ))}
                  </div>
                  <p className="t-cap mut" style={{ marginTop: 6 }}>
                    the currency itself is chosen in Facility
                  </p>
                </Card>
              </>
            ) : null}

            {showShares ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Doctor payout shares</p>
                {narrow ? (
                  <Card>
                    {doctors.map((d, i) => {
                      const share = shown.payoutShares[d.id];
                      return (
                        <div key={d.id} className="flex"
                          style={{ gap: 10, alignItems: 'center', padding: '8px 0',
                            borderTop: i ? '1px solid var(--stroke-200)' : undefined }}>
                          <p className="t-strong grow">{d.name}</p>
                          <DChip onClick={() => setShareFor(d.id)}>
                            {share === undefined ? 'Set' : `${share}%`} ✎
                          </DChip>
                        </div>
                      );
                    })}
                    {doctors.length ? <p className="t-cap mut mt8">computed on collected money only</p> : null}
                  </Card>
                ) : (
                <div className="cm-tscroll">
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th style={{ width: '58%' }}>Doctor</th>
                        <th style={{ width: '26%' }}>Computed on</th>
                        <th style={{ width: '16%' }}>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doctors.map((d) => {
                        const share = shown.payoutShares[d.id];
                        return (
                          <tr key={d.id} className="rowc">
                            <td>
                              <div>
                                <p className="t-strong">{d.name}</p>
                                <p className="t-cap mut">monthly · cut-off the last day</p>
                              </div>
                            </td>
                            <td><span className="mut">collected money only</span></td>
                            <td>
                              <DChip onClick={() => setShareFor(d.id)}>
                                {share === undefined ? 'Set' : `${share}%`} ✎
                              </DChip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
                {!doctors.length ? (
                  <p className="t-sec mut" style={{ marginTop: 12 }}>
                    No doctor holds a login in this clinic yet. A share is a property of a person,
                    so the table fills as doctors are added under Roles &amp; access.
                  </p>
                ) : null}
                <p className="t-cap mut" style={{ marginTop: 6 }}>
                  a share moves from the next run onward — it never rewrites a run already approved
                </p>
              </>
            ) : null}

            {showPolicy ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Policy at the desk</p>
                <div className="cm-fields" style={{ display: 'flex', gap: 8 }}>
                  <Card style={{ flex: 1, margin: 0 }}>
                    <p className="t-ovl mut mb8">Discount policy</p>
                    <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Chip kind={shown.discountFlag === 'all' ? 'brand' : 'outline'} small
                        className={shown.discountFlag === 'all' ? 'w6' : undefined}
                        onClick={() => patch({ discountFlag: 'all' })}>
                        Flag every discount
                      </Chip>
                      <Chip kind={shown.discountFlag === 'above' ? 'brand' : 'outline'} small
                        className={shown.discountFlag === 'above' ? 'w6' : undefined}
                        onClick={() => patch({ discountFlag: 'above' })}>
                        Flag above {shown.discountThresholdPercent}%
                      </Chip>
                    </div>
                    <p className="t-cap mut" style={{ marginTop: 6 }}>
                      what the audit log raises to you, and what the desk may do unasked
                    </p>
                  </Card>
                  <Card style={{ flex: 1, margin: 0 }}>
                    <p className="t-ovl mut mb8">Day close</p>
                    <p className="t-sec sec">
                      Unclosed-day reminder{' '}
                      <button type="button" className="w6 ink"
                        style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                        onClick={() => setEditing('dayClose')}>
                        {shown.dayCloseReminder ? clock12(shown.dayCloseReminder) : 'Not set'} <Icon name="pencil" size="sm" />
                      </button>
                    </p>
                    <div className="flex" style={{ gap: 12, marginTop: 10 }}>
                      <p className="t-sec grow">Reopening a locked day needs your approval</p>
                      <label style={{ display: 'inline-flex', cursor: 'pointer' }}>
                        <input type="checkbox" className="sr-only"
                          aria-label="Reopening a locked day needs your approval"
                          checked={shown.reopenNeedsApproval}
                          onChange={(e) => patch({ reopenNeedsApproval: e.target.checked })} />
                        <span className={shown.reopenNeedsApproval ? 'tgl on' : 'tgl'} aria-hidden="true" />
                      </label>
                    </div>
                  </Card>
                </div>
              </>
            ) : null}

            {!anyShown ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No policy on this screen matches that. Clear the search to see Money again.
              </p>
            ) : null}

            <Card kind="accent">
              <p className="t-sec brand">
                Insurer rates live in Billing ▸ Insurers, gated by your role, and a catalog price
                change pings you rather than applying quietly.{' '}
                <button type="button" className="w6 brand"
                  style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                  onClick={() => setExplaining(true)}>
                  Open Billing ▸ Insurers ›
                </button>
              </p>
            </Card>

            {bad ? <p className="t-sec red" style={{ marginTop: 10 }}>{bad}</p> : null}
            {save.isError ? (
              <p className="t-sec red" style={{ marginTop: 10 }}>
                That did not save. Nothing was changed on the server — try again, or reload to see
                what is stored.
              </p>
            ) : null}
            {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
          </>
        ) : null}
      </div>

      <Modal
        open={explaining}
        onClose={() => setExplaining(false)}
        title="Billing ▸ Insurers"
        sub="where an insurer's rates are set"
        width={460}
        foot={<button type="button" className="btn btn-outline" onClick={() => setExplaining(false)}>Close</button>}
      >
        <div style={{ marginTop: 12 }}>
          <p className="t-sec sec">
            An insurer’s rate card — what each service is reimbursed at, and the deduction the
            payer applies — is a billing screen, not a settings one. What this clinic has filed
            is under Reports ▸ Insurance &amp; receivables: every claim, what was approved, and
            what is still owed.
          </p>
          <p className="t-cap mut mt8">
            A claim carries no rate, no deduction line and no rejection reason, which is why that
            report says “not recorded” where those belong.
          </p>
        </div>
      </Modal>

      <TextEditor
        open={editing !== null}
        title={editing === 'taxId' ? 'Tax ID' : editing === 'dayClose' ? 'Unclosed-day reminder' : 'VAT'}
        sub={editing === 'taxId' ? 'the number printed on every tax invoice'
          : editing === 'dayClose'
            ? '24-hour (23:00). Stored as this clinic’s policy — there is no day-close run to fire on it yet'
            : 'the percentage a taxed line is charged, 0–100. Stored here; the bill form still carries its own rate until the backlog item lands'}
        value={editing === 'taxId' ? (shown?.taxId ?? '')
          : editing === 'dayClose' ? (shown?.dayCloseReminder ?? '')
            : shown?.vatPercent === null || shown?.vatPercent === undefined ? '' : String(shown.vatPercent)}
        onClose={() => setEditing(null)}
        onDone={(next) => {
          if (editing === 'taxId') { setBad(null); patch({ taxId: next || null }); return; }
          if (editing === 'dayClose') {
            if (next && !/^([01]\d|2[0-3]):[0-5]\d$/.test(next)) {
              setBad('A reminder time is HH:MM on a 24-hour clock — 23:00.');
              return;
            }
            setBad(null);
            patch({ dayCloseReminder: next || null });
            return;
          }
          const n = Number(next);
          if (next && (!Number.isFinite(n) || n < 0 || n > 100)) {
            setBad('A VAT rate is a percentage between 0 and 100.');
            return;
          }
          setBad(null);
          patch({ vatPercent: next ? n : null });
        }}
      />

      <TextEditor
        open={shareFor !== null}
        title="Payout share"
        sub="the percentage of collected money this doctor is paid, 0–100"
        value={shareFor && shown?.payoutShares[shareFor] !== undefined
          ? String(shown.payoutShares[shareFor]) : ''}
        onClose={() => setShareFor(null)}
        onDone={(next) => {
          const id = shareFor;
          if (!id || !shown) return;
          const n = Number(next);
          if (!next || !Number.isFinite(n) || n < 0 || n > 100) {
            setBad('A share is a percentage between 0 and 100.');
            return;
          }
          setBad(null);
          patch({ payoutShares: { ...shown.payoutShares, [id]: n } });
        }}
      />
    </div>
  );
}
